// Ionic & Angular Imports
import {Component} from '@angular/core';
import {FormBuilder, Validators, FormGroup} from '@angular/forms';
import {Events, AlertController, PopoverController, Popover, ViewController, 
        NavParams, LoadingController, Loading} from 'ionic-angular';

// Service Imports
import {rsReportSvc} from '../../../../../services/report.service';
import {rsEquipmentSvc, Equipment} from '../../../../../services/equipment.service';
import {EquipmentType} from '../../../../../services/equipmenttype.service';

// Component Imports
import {BasePage} from '../../../../base.component';
import {EquipmentPopover} from '../equipment-popover/equipment-popover.component';

@Component({
    templateUrl: 'equipment-modal.html',
    providers: [
        rsEquipmentSvc
    ]
})
export class EquipmentModal extends BasePage
{
    // Class member variables
    private modalForm      : FormGroup;
    private editMode       : boolean;
    private initEquip      : Equipment;
    private equiptypeList  : EquipmentType[];

    constructor( 
        protected alertCtrl : AlertController,
        private navParams : NavParams, 
        private reportSvc: rsReportSvc, 
        private equipmentSvc: rsEquipmentSvc,
        private loadingCtrl : LoadingController,
        private viewCtrl : ViewController,
        private formBuilder : FormBuilder,
        private popupCtrl : PopoverController,
        private eventSystem : Events
    ){
        // Construct base class
        super( alertCtrl );

        // Retrieve modal data from NavParams           
        this.editMode = this.navParams.get("editmode");
        this.equiptypeList = this.navParams.get("equiptype");
        if( this.editMode ) { 
            this.initEquip = <Equipment>this.navParams.get("equipment");
        } else {
            // Initialize our activity object
            this.initEquip = <Equipment>{
                id: 0,
                equipment : {
                    id: 0,
                    name: "",
                    description: ""
                },
                notes: ""
            };
        }

        // Build our form model structure
        this.buildFormModel();
    }

    // Class method for builidng our form model structure 
    private buildFormModel() {
        let name : string = "";
        if( this.initEquip.equipment.id > 0 ) {
            name = this.initEquip.equipment.name
        }
        this.modalForm = this.formBuilder.group({
            name:               [name, Validators.required],
            equipmenttypeid:    [this.initEquip.equipment.id, Validators.required],
            notes:              [this.initEquip.notes]
        });
    }

    // Class helper method for setting a field in the form to a specified value
    private setFormField( fieldName: string, fieldValue: any ) {
        this.modalForm.controls[fieldName].setValue( fieldValue );
        this.modalForm.controls[fieldName].markAsDirty();
    }

    public selectEquipmentType(event) {
        // Create our popover instance
        let popover : Popover = this.popupCtrl.create( EquipmentPopover, {
            equiptype: this.equiptypeList
        }, {
            cssClass: "customPopover"
        } );
        
        // Handle popover callback
        popover.onWillDismiss( (data) => {
            // If there was a selection made
            if( data !== null && data.selected !== undefined ) {
                let equiptype : EquipmentType = data.selected;
                this.setFormField( 'equipmenttypeid', equiptype.id );
                this.setFormField( 'name', equiptype.name );
            }
        });    

        // Present our popover component 
        popover.present({
            ev: event
        });    
    }

    // Class method to close the modal view 
    public modalClose() {
        this.viewCtrl.dismiss();
    }

    public modalReset() {
        let name : string = "";
        if( this.initEquip.equipment.id > 0 ) {
            name = this.initEquip.equipment.name;
        }        
        this.modalForm.reset( {
            name:               name,
            equipmenttypeid:    this.initEquip.equipment.id,
            notes:              this.initEquip.notes              
        });
    }

    public modalSubmit( formData : any, isValid : boolean ) {
        // Display a loader while the modal submits our changes
        let loader : Loading = this.loadingCtrl.create({
            content: this.editMode ? 'Updating...' : 'Adding...',
            dismissOnPageChange: true
        });
        loader.present().then( () => {
            // Determine what the submit button should be doing based on the validity
            // of the form and the mode of the modal.
            if( isValid === true && !this.editMode) {
                // The submit button should save a new subcontractor
                this.equipmentSvc.addEquipment( formData.equipmenttypeid, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    console.log('add result:  ', result);
                    this.eventSystem.publish('equipment:added', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error adding this equipment to the report.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                });
            } else if( isValid === true && this.editMode ) {
                // The submit button should save changes to an existing subcontractor
                this.equipmentSvc.updateEquipment( this.initEquip.id, formData.equipmenttypeid, formData.notes, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    console.log( 'update result:  ', result );
                    this.eventSystem.publish('equipment:updated', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error updating this equipment.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                }); 
            }
        });
    }

    public modalDelete() {
        // Verify that we are in the correct editMode and that our id is valid
        if( this.editMode && this.initEquip.id > 0 )
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Equipment', 'Are you sure you want to delete "' + this.initEquip.equipment.name + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.equipmentSvc.deleteEquipment( this.initEquip.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('equipment:deleted', this.initEquip );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this equipment.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        this.viewCtrl.dismiss().then( () => {
                            loader.dismiss();
                        });
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }    
}    


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/equipment/equipment-modal/equipment-modal.component.ts